import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FiArrowLeft,
  FiCheck,
  FiClock,
  FiFileText,
  FiDownload,
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiExternalLink,
  FiCheckCircle,
  FiXCircle,
  FiPaperclip,
  FiActivity,
  FiChevronDown,
  FiChevronUp,
  FiAlertCircle,
  FiEye,
} from "react-icons/fi";
import { HiOutlineCurrencyRupee } from "react-icons/hi";

import RightWrapper from "../../../Components/CommonComponents/ComponentWrapper";
import CustomSpinner from "../../../Components/CommonComponents/CustomSpinner";
import ConfirmDialog from "../../../Components/CommonComponents/ConfirmDialog";
import Modal from "../../../Components/UI/Modal";
import Button from "../../../Components/UI/Button";
import { END_POINT, getJwtToken } from "../../../Redux/AdminReducer/action";
import { useCheckRole } from "../../../UtilityHookFunctions/LoggedInUser";
import { DownloadFile } from "../../../UtilityHookFunctions/OtherMongoHelperFunctions";
import TherapyRequestDetails, { OriginBadge, StatusBadge } from "../Alternative_Therapies/TherapyRequestDetails";

const errorText = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong";

const fetchItems = async (path) => {
  try {
    return await axios.get(`${END_POINT}/${path}`, {
      headers: { Authorization: getJwtToken() },
    });
  } catch (error) {
    toast.error(errorText(error));
  }
};

const isImageFile = (filename = "") =>
  /\.(png|jpe?g|webp|gif|svg)$/i.test(filename || "");

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const ReviewBid = () => {
  const { bid_id } = useParams();
  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [showFullRequestForm, setShowFullRequestForm] = useState(false);

  // File Preview Lightbox Modal
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    url: null,
    title: "",
    isImage: false,
  });

  const nav = useNavigate();
  const checkRole = useCheckRole();
  const isDoctor = checkRole ? checkRole("maindoctor") : true;

  useEffect(() => {
    setLoading(true);
    fetchItems(`bid/fullBid/${bid_id}`)
      .then((res) => {
        setBid(res?.data || null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [bid_id]);

  const therapy = bid?.bidOn;
  const bidder = bid?.bidder;
  const canAccept = isDoctor && bid?.status === "bid" && therapy?.status === "bid";

  const sampleFileName = bid?.sample?.name;
  const sampleFileId = bid?.sample?.id;
  const sampleFileUrl = sampleFileId ? `${END_POINT}/bid/download/${sampleFileId}` : null;
  const sampleIsImg = isImageFile(sampleFileName);

  const handleAcceptBid = async () => {
    if (!bid || !therapy?._id) return;
    setIsSubmitting(true);
    const toastId = toast.loading("Accepting bid proposal...");
    try {
      await axios.patch(
        `${END_POINT}/alternative_therapy/approve_consultant/${therapy._id}`,
        { bid: bid._id },
        { headers: { Authorization: getJwtToken() } }
      );
      toast.dismiss(toastId);
      toast.success("Bid accepted successfully! Therapy assigned to consultant.", {
        autoClose: 2000,
      });
      setIsAcceptModalOpen(false);
      setTimeout(() => nav("/Bids"), 1000);
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(errorText(error), { autoClose: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openFilePreview = (url, title, isImage) => {
    if (!url) return;
    setPreviewModal({
      isOpen: true,
      url,
      title,
      isImage,
    });
  };

  const getStatusBadge = () => {
    switch (bid?.status) {
      case "win":
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
            <FiCheckCircle className="h-3.5 w-3.5 text-emerald-600" />
            Accepted & Assigned
          </span>
        );
      case "lose":
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
            <FiXCircle className="h-3.5 w-3.5 text-slate-500" />
            Not Selected
          </span>
        );
      case "bid":
      default:
        return (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
            <FiClock className="h-3.5 w-3.5 text-amber-600" />
            Pending Review
          </span>
        );
    }
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    if (!parts.length || !parts[0]) return "CO";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const dailyRate =
    bid?.duration && bid?.bidPrice != null && Number(bid.duration) > 0
      ? (Number(bid.bidPrice) / Number(bid.duration)).toFixed(2)
      : null;

  return (
    <RightWrapper>
      <div className="w-full px-2 py-4 md:px-4 space-y-6">
        {/* Navigation & Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => nav(-1)}
              aria-label="Go back"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 active:scale-95"
            >
              <FiArrowLeft className="h-4 w-4" />
            </button>

            <div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                <Link to="/Bids" className="hover:text-sky-600 transition">
                  Alternative Therapies
                </Link>
                <span>/</span>
                <Link to="/Bids" className="hover:text-sky-600 transition">
                  Bids
                </Link>
                <span>/</span>
                <span className="text-sky-600 font-semibold">Review Proposal</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2.5">
                <h1 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900 capitalize">
                  {therapy?.therapy ? `${therapy.therapy} Proposal Review` : "Bid Proposal Review"}
                </h1>
                {getStatusBadge()}
                {therapy?.status && <StatusBadge status={therapy.status} />}
                {therapy?.origin && <OriginBadge origin={therapy.origin} />}
              </div>
            </div>
          </div>

          {/* Top Actions */}
          <div className="flex items-center gap-3 self-end sm:self-center">
            {therapy?._id && (
              <button
                type="button"
                onClick={() => nav(`/Bids/${therapy._id}`)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-sky-600 active:scale-95"
              >
                <FiExternalLink className="h-4 w-4" />
                <span>View Request</span>
              </button>
            )}

            {canAccept && (
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsAcceptModalOpen(true)}
                icon={<FiCheck className="h-4 w-4" />}
                iconPosition="left"
              >
                Accept Bid
              </Button>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center">
            <CustomSpinner />
            <p className="mt-3 text-sm text-slate-500">Loading proposal details...</p>
          </div>
        ) : !bid ? (
          /* Empty / Not Found State */
          <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
              <FiAlertCircle className="h-7 w-7" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">Bid Proposal Not Found</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
              The bid proposal you are trying to review could not be retrieved or has been removed.
            </p>
            <div className="mt-6">
              <Button variant="primary" onClick={() => nav("/Bids")}>
                Back to Bids
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Top Metric Cards (Full Width 4-column Grid) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Proposed Price */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Offered Total Fee
                  </span>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                    <HiOutlineCurrencyRupee className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-slate-900">
                    ₹{bid?.bidPrice != null ? Number(bid.bidPrice).toLocaleString("en-IN") : "—"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {dailyRate ? `Approx. ₹${dailyRate} / day` : "Total quoted for assignment"}
                </p>
              </div>

              {/* Proposed Duration */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Proposed Duration
                  </span>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                    <FiClock className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-slate-900">
                    {bid?.duration ? `${bid.duration} Days` : "—"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {therapy?.duration ? `Doctor's expectation: ${therapy.duration} days` : "Proposed treatment length"}
                </p>
              </div>

              {/* Submitted Date */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Bid Placed On
                  </span>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <FiCalendar className="h-5 w-5" />
                  </div>
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-lg font-bold text-slate-900">
                    {formatDate(bid?.bidDate)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">Proposal submission date</p>
              </div>

              {/* Status Indicator */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Decision Status
                  </span>
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                    {bid?.status === "win" ? (
                      <FiCheckCircle className="h-5 w-5 text-emerald-600" />
                    ) : bid?.status === "lose" ? (
                      <FiXCircle className="h-5 w-5 text-slate-500" />
                    ) : (
                      <FiClock className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-lg font-bold capitalize text-slate-900">
                    {bid?.status === "win"
                      ? "Approved & Won"
                      : bid?.status === "lose"
                      ? "Not Selected"
                      : "Pending Decision"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {bid?.status === "win"
                    ? "Consultant currently assigned"
                    : bid?.status === "lose"
                    ? "Another proposal was chosen"
                    : canAccept
                    ? "Awaiting your decision"
                    : "Under doctor review"}
                </p>
              </div>
            </div>

            {/* Main Content Layout (12 Columns) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column (8 cols): Proposal details & Therapy Request context */}
              <div className="lg:col-span-8 space-y-6">
                {/* Consultant Proposal & Cover Note */}
                <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 bg-slate-50/50">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center">
                        <FiFileText className="h-4 w-4" />
                      </div>
                      <h2 className="text-sm font-semibold tracking-tight text-slate-900">
                        Consultant's Treatment Approach & Note
                      </h2>
                    </div>
                    <span className="text-xs font-mono font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                      ID: {bid?._id ? `${bid._id.slice(-6)}` : "—"}
                    </span>
                  </div>

                  <div className="p-6 space-y-5">
                    {/* Proposal Description */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        Cover Note & Methodology
                      </h3>
                      {bid?.description ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap">
                          {bid.description}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-slate-200 p-4 text-sm italic text-slate-400">
                          No written note was provided with this proposal.
                        </div>
                      )}
                    </div>

                    {/* Attached Sample File with Image Thumbnail Preview */}
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                        Proposal Document / Case Attachment
                      </h3>
                      {sampleFileId ? (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 p-4 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100/70 transition">
                          <div className="flex items-center gap-3.5 min-w-0">
                            {sampleIsImg && sampleFileUrl ? (
                              <div
                                onClick={() => openFilePreview(sampleFileUrl, sampleFileName, true)}
                                className="relative h-14 w-14 rounded-xl overflow-hidden border border-slate-200 bg-white shrink-0 cursor-pointer group shadow-sm"
                                title="Click to view full preview"
                              >
                                <img
                                  src={sampleFileUrl}
                                  alt={sampleFileName}
                                  className="h-full w-full object-cover group-hover:scale-105 transition duration-200"
                                />
                                <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                                  <FiEye className="h-4 w-4" />
                                </div>
                              </div>
                            ) : (
                              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
                                <FiPaperclip className="h-6 w-6" />
                              </div>
                            )}

                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-slate-900" title={sampleFileName}>
                                {sampleFileName}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                Document attached with consultant proposal
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                            {sampleFileUrl && (
                              <button
                                type="button"
                                onClick={() => openFilePreview(sampleFileUrl, sampleFileName, sampleIsImg)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm transition active:scale-95"
                              >
                                <FiEye className="h-3.5 w-3.5 text-slate-500" />
                                <span>Preview</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => DownloadFile("bid/download", sampleFileId, sampleFileName)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-sm transition active:scale-95"
                            >
                              <FiDownload className="h-3.5 w-3.5 text-slate-500" />
                              <span>Download</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 p-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/40 text-slate-400">
                          <FiPaperclip className="h-5 w-5 text-slate-300" />
                          <span className="text-sm">No sample file attached with this bid proposal.</span>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                {/* Associated Therapy Request Card */}
                {therapy && (
                  <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 bg-slate-50/50">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-sky-100 text-sky-700 flex items-center justify-center">
                          <FiActivity className="h-4 w-4" />
                        </div>
                        <h2 className="text-sm font-semibold tracking-tight text-slate-900">
                          Therapy Request Case Brief
                        </h2>
                      </div>
                      {therapy?.status && <StatusBadge status={therapy.status} />}
                    </div>

                    <div className="p-6 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3.5">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Therapy Category</p>
                          <p className="mt-1 text-sm font-bold text-slate-900 capitalize">
                            {therapy.therapy || "—"}
                          </p>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3.5">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Requested Timeline
                          </p>
                          <p className="mt-1 text-sm font-bold text-slate-900">
                            {therapy.duration ? `${therapy.duration} Days` : "—"}
                          </p>
                        </div>
                        <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-3.5">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Referral Origin</p>
                          <p className="mt-1 text-sm font-bold text-slate-900 capitalize">
                            {therapy.origin === "doctor" ? "Doctor Referral" : "Patient Direct"}
                          </p>
                        </div>
                      </div>

                      {/* Doctor's Brief / Note */}
                      {therapy.note && (
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                            Doctor's Special Instructions
                          </p>
                          <p className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">
                            {therapy.note}
                          </p>
                        </div>
                      )}

                      {/* Patient Details Snapshot */}
                      {therapy?.requestForm?.fullName && (
                        <div className="rounded-xl border border-slate-200 bg-sky-50/40 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <FiUser className="h-4 w-4 text-sky-700" />
                            <h4 className="text-xs font-bold uppercase tracking-wider text-sky-800">
                              Patient Snapshot
                            </h4>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <div>
                              <span className="text-slate-500 font-medium">Name: </span>
                              <span className="font-semibold text-slate-900">{therapy.requestForm.fullName}</span>
                            </div>
                            {therapy.requestForm.mobile && (
                              <div>
                                <span className="text-slate-500 font-medium">Contact: </span>
                                <span className="font-semibold text-slate-900">{therapy.requestForm.mobile}</span>
                              </div>
                            )}
                            {therapy.requestForm.chiefComplaint && (
                              <div className="sm:col-span-2">
                                <span className="text-slate-500 font-medium">Chief Complaint: </span>
                                <span className="text-slate-800">{therapy.requestForm.chiefComplaint}</span>
                              </div>
                            )}
                            {therapy.requestForm.treatmentGoal && (
                              <div className="sm:col-span-2">
                                <span className="text-slate-500 font-medium">Treatment Goal: </span>
                                <span className="text-slate-800">{therapy.requestForm.treatmentGoal}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Expandable Clinical Details */}
                      {therapy?.requestForm && (
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={() => setShowFullRequestForm((prev) => !prev)}
                            className="inline-flex items-center gap-2 text-xs font-semibold text-sky-700 hover:text-sky-800 transition"
                          >
                            <span>
                              {showFullRequestForm
                                ? "Hide Complete Patient Questionnaire"
                                : "View Complete Patient Questionnaire Details"}
                            </span>
                            {showFullRequestForm ? (
                              <FiChevronUp className="h-4 w-4" />
                            ) : (
                              <FiChevronDown className="h-4 w-4" />
                            )}
                          </button>

                          {showFullRequestForm && (
                            <div className="mt-3">
                              <TherapyRequestDetails requestForm={therapy.requestForm} title="Clinical Details" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                )}
              </div>

              {/* Right Column (4 cols): Consultant Profile & Decision Card */}
              <div className="lg:col-span-4 space-y-6">
                {/* Consultant Profile Card */}
                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="text-center">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-tr from-sky-600 to-indigo-500 text-white shadow-md text-xl font-bold">
                      {getInitials(bidder?.name)}
                    </div>
                    <h3 className="mt-3 text-lg font-bold text-slate-900">
                      {bidder?.name || "Consultant"}
                    </h3>
                    {bidder?.typesOfDoctor && (
                      <span className="mt-1 inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700 capitalize ring-1 ring-inset ring-sky-200">
                        {bidder.typesOfDoctor} Specialist
                      </span>
                    )}
                  </div>

                  <div className="mt-6 border-t border-slate-100 pt-5 space-y-3.5">
                    {bidder?.consultantId && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 font-medium">Consultant ID</span>
                        <span className="font-mono font-semibold text-slate-800">{bidder.consultantId}</span>
                      </div>
                    )}

                    {bidder?.email && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                          <FiMail className="h-4 w-4" />
                        </div>
                        <a
                          href={`mailto:${bidder.email}`}
                          className="truncate text-slate-700 hover:text-sky-600 font-medium transition"
                          title={bidder.email}
                        >
                          {bidder.email}
                        </a>
                      </div>
                    )}

                    {bidder?.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                          <FiPhone className="h-4 w-4" />
                        </div>
                        <a
                          href={`tel:${bidder.phone}`}
                          className="text-slate-700 hover:text-sky-600 font-medium transition"
                        >
                          {bidder.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </section>

                {/* Doctor's Decision Card */}
                <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-slate-900 mb-2">
                    Proposal Decision
                  </h3>

                  {bid?.status === "win" && (
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-800 space-y-2">
                      <div className="flex items-center gap-2">
                        <FiCheckCircle className="h-5 w-5 text-emerald-600 shrink-0" />
                        <span className="font-bold text-sm">Proposal Accepted</span>
                      </div>
                      <p className="text-xs text-emerald-700 leading-relaxed">
                        This bid proposal was accepted. The therapy has been assigned to{" "}
                        <strong className="font-semibold">{bidder?.name || "this consultant"}</strong> and is now in treatment stage.
                      </p>
                    </div>
                  )}

                  {bid?.status === "lose" && (
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-slate-700 space-y-2">
                      <div className="flex items-center gap-2">
                        <FiXCircle className="h-5 w-5 text-slate-500 shrink-0" />
                        <span className="font-bold text-sm">Not Selected</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Another proposal was chosen for this therapy request. This bid is closed.
                      </p>
                    </div>
                  )}

                  {canAccept && (
                    <div className="space-y-4">
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Accepting will award the assignment to{" "}
                        <strong className="text-slate-900">{bidder?.name || "the consultant"}</strong> at the
                        agreed fee of{" "}
                        <strong className="text-slate-900">
                          ₹{Number(bid.bidPrice || 0).toLocaleString("en-IN")}
                        </strong>{" "}
                        for <strong className="text-slate-900">{bid.duration} days</strong>. Competing bids will be
                        marked as not selected.
                      </p>

                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => setIsAcceptModalOpen(true)}
                        isLoading={isSubmitting}
                        className="w-full"
                        icon={<FiCheck className="h-4 w-4" />}
                        iconPosition="left"
                      >
                        Accept & Assign Bid
                      </Button>
                    </div>
                  )}

                  {!canAccept && bid?.status === "bid" && (
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-amber-800 space-y-2">
                      <div className="flex items-center gap-2">
                        <FiClock className="h-5 w-5 text-amber-600 shrink-0" />
                        <span className="font-bold text-sm">Under Review</span>
                      </div>
                      <p className="text-xs text-amber-700 leading-relaxed">
                        {!isDoctor
                          ? "This proposal is currently awaiting approval from the supervising doctor."
                          : "This therapy request is no longer accepting decisions."}
                      </p>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirmation Dialog before accepting */}
      <ConfirmDialog
        isOpen={isAcceptModalOpen}
        title="Accept Bid Proposal?"
        message={`Are you sure you want to accept this proposal from ${
          bidder?.name || "the consultant"
        } for ₹${Number(bid?.bidPrice || 0).toLocaleString("en-IN")} (${
          bid?.duration || 0
        } days)? This will assign the therapy to them and conclude bidding.`}
        confirmLabel="Accept & Assign"
        cancelLabel="Review Later"
        destructive={false}
        isProcessing={isSubmitting}
        onConfirm={handleAcceptBid}
        onCancel={() => setIsAcceptModalOpen(false)}
      />

      {/* File Preview Modal */}
      <Modal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, url: null, title: "", isImage: false })}
        title={previewModal.title || "Document Preview"}
        maxWidth="max-w-3xl"
      >
        <div className="flex flex-col items-center justify-center p-2">
          {previewModal.isImage ? (
            <img
              src={previewModal.url}
              alt={previewModal.title || "File Preview"}
              className="max-h-[65vh] w-auto max-w-full rounded-xl object-contain shadow-sm border border-slate-200 bg-slate-50"
            />
          ) : (
            <iframe
              src={previewModal.url}
              title={previewModal.title}
              className="w-full h-[65vh] rounded-xl border border-slate-200"
            />
          )}

          <div className="mt-4 flex items-center justify-between w-full pt-3 border-t border-slate-100">
            <span className="text-xs text-slate-500 truncate max-w-md" title={previewModal.title}>
              {previewModal.title}
            </span>
            <div className="flex items-center gap-2">
              <a
                href={previewModal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
              >
                <FiExternalLink className="h-3.5 w-3.5" />
                <span>Open in Tab</span>
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreviewModal({ isOpen: false, url: null, title: "", isImage: false })}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </RightWrapper>
  );
};

export default ReviewBid;

